/**
 * Shared folder memberships: the folder list (with the caller's role, color
 * and lock state) plus the REST client for folder lifecycle. The mounting of
 * folder contents as data contexts lives in store.ts, driven by subscribing
 * to this store.
 */
import { create } from "zustand";
import type { SharedFolderColor, SharedFolderDto, SharedFolderInviteDto, SharedFolderMemberDto, SharedRole } from "@hydra-notes/shared";

export type SharedFolderMeta = SharedFolderDto;

/** Palette key -> css variable. Colors live in tokens.css only (invariant 5). */
export const SHARED_PALETTE: Record<SharedFolderColor, string> = {
  gray: "var(--sf-gray)",
  red: "var(--sf-red)",
  orange: "var(--sf-orange)",
  yellow: "var(--sf-yellow)",
  green: "var(--sf-green)",
  teal: "var(--sf-teal)",
  blue: "var(--sf-blue)",
  purple: "var(--sf-purple)",
  pink: "var(--sf-pink)",
};

export function sharedAccent(colorKey: string): string {
  return SHARED_PALETTE[colorKey as SharedFolderColor] ?? SHARED_PALETTE.gray;
}

interface SharedFoldersState {
  status: "idle" | "loading" | "ready";
  /** All memberships, locked ones included (the tree shows them greyed). */
  folders: SharedFolderMeta[];
  refresh: () => Promise<void>;
  /** Immediate local lock (sync refused mid-session); refresh confirms. */
  markLocked: (folderId: string) => void;
  removeLocal: (folderId: string) => void;
}

export const useSharedFolders = create<SharedFoldersState>((set, get) => ({
  status: "idle",
  folders: [],

  refresh: async () => {
    if (get().status === "idle") set({ status: "loading" });
    try {
      const res = await fetch("/api/shared-folders", { credentials: "same-origin" });
      if (!res.ok) {
        // signed out or api down: empty is the safe render state
        if (res.status === 401) set({ status: "ready", folders: [] });
        return;
      }
      const data = (await res.json()) as { folders: SharedFolderMeta[] };
      set({ status: "ready", folders: data.folders });
    } catch {
      // offline: keep whatever we had; shared folders are online-only anyway
      set((s) => ({ status: s.status === "loading" ? "ready" : s.status }));
    }
  },

  markLocked: (folderId) =>
    set((s) => ({
      folders: s.folders.map((f) => (f.id === folderId && f.role !== "owner" ? { ...f, locked: true } : f)),
    })),

  removeLocal: (folderId) => set((s) => ({ folders: s.folders.filter((f) => f.id !== folderId) })),
}));

export function useFolderMeta(folderId: string | null | undefined): SharedFolderMeta | undefined {
  return useSharedFolders((s) => (folderId ? s.folders.find((f) => f.id === folderId) : undefined));
}

// ---------- REST ----------

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `request failed (${res.status})`);
  return data;
}

export const sharedFoldersApi = {
  create: (title: string, colorKey: SharedFolderColor) =>
    req<{ folder: SharedFolderMeta }>("/api/shared-folders", {
      method: "POST",
      body: JSON.stringify({ title, colorKey }),
    }),

  detail: (id: string) => req<{ folder: SharedFolderMeta; members: SharedFolderMemberDto[] }>(`/api/shared-folders/${id}`),

  update: (id: string, patch: { title?: string; colorKey?: SharedFolderColor }) =>
    req<{ ok: true }>(`/api/shared-folders/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  remove: (id: string) => req<{ ok: true }>(`/api/shared-folders/${id}`, { method: "DELETE" }),

  setMemberRole: (id: string, userId: string, role: Exclude<SharedRole, "owner">) =>
    req<{ ok: true }>(`/api/shared-folders/${id}/members/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),

  removeMember: (id: string, userId: string) =>
    req<{ ok: true }>(`/api/shared-folders/${id}/members/${userId}`, { method: "DELETE" }),

  leave: (id: string) => req<{ ok: true }>(`/api/shared-folders/${id}/leave`, { method: "POST" }),

  invite: (id: string, email: string, role: Exclude<SharedRole, "owner">) =>
    req<{ ok: boolean; queued?: boolean; link?: string }>(`/api/shared-folders/${id}/invites`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  invites: (id: string) => req<{ invites: SharedFolderInviteDto[] }>(`/api/shared-folders/${id}/invites`),

  revokeInvite: (id: string, token: string) =>
    req<{ ok: true }>(`/api/shared-folders/${id}/invites/${token}`, { method: "DELETE" }),

  accept: (token: string) =>
    req<{ ok: true; folderId: string }>("/api/shared-folders/accept", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  /** Full-text search over unlocked shared folders (note_index, server-side). */
  search: (q: string) =>
    req<{ hits: { spaceId: string; noteId: string; title: string; snippet: string; updatedAt: number }[] }>(
      `/api/shared-folders/search?q=${encodeURIComponent(q)}`,
    ),
};
